import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Form, Spinner, Dropdown } from "@themesberg/react-bootstrap";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchApi } from "../../redux/api";
import { formatNumber } from "../../redux/utils";
import CustomTable from "../../components/CustomTable";
import DaterangeSelector from "../../components/DaterangeSelector";
import ResponsiveContainer from "../../components/ResponsiveContainer";

function TaxReport() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedTax, setSelectedTax] = useState("");
  const [taxOptions, setTaxOptions] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchApi("tax-report/taxes")
      .then((resp) => {
        if (resp.success && resp.results) {
          setTaxOptions(resp.results);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedTax) params.append("tax", selectedTax);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    if (search) params.append("search", search);

    fetchApi(`tax-report?${params.toString()}`)
      .then((resp) => {
        if (resp.success && resp.results) {
          setRecords(resp.results);
        } else {
          setRecords([]);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [selectedTax, startDate, endDate, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = (from, to) => {
    setStartDate(from);
    setEndDate(to);
  };

  const exportData = records.map((r, i) => ({
    "S/N": i + 1,
    "PV Code": r.pv_code,
    Description: r.description,
    "Contractor Code": String(r.contractor_code).padStart(3, "0"),
    "Contract No": r.contract_no,
    Amount: parseFloat(r.amount || 0),
  }));

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    FileSaver.saveAs(data, "tax-report.xlsx");
  };

  const exportToPdf = () => {
    const doc = new jsPDF();
    doc.text("Tax Report", 14, 15);
    autoTable(doc, {
      head: [["S/N", "PV Code", "Description", "Contractor Code", "Contract No", "Amount"]],
      body: exportData.map((r) => [
        r["S/N"],
        r["PV Code"],
        r.Description,
        r["Contractor Code"],
        r["Contract No"],
        r.Amount,
      ]),
      startY: 22,
    });
    doc.save("tax-report.pdf");
  };

  const fields = [
    {
      title: "S/N",
      custom: true,
      component: (item) => records.indexOf(item) + 1,
    },
    {
      title: "PV Code",
      value: "pv_code",
    },
    {
      title: "Description",
      value: "description",
    },
    {
      title: "Contractor Code",
      custom: true,
      className: "text-center",
      component: (item) => String(item.contractor_code).padStart(3, "0"),
    },
    {
      title: "Contract No",
      value: "contract_no",
    },
    {
      title: "Amount",
      value: "amount",
      custom: true,
      component: (item) => (
        <span style={{ textAlign: "right", display: "block" }}>
          ₦{formatNumber(item.amount)}
        </span>
      ),
    },
  ];

  return (
    <ResponsiveContainer>
      <Card>
        <Card.Body>
          <Row className="align-items-center mb-3">
            <Col>
              <h5 className="mb-0">Tax Report</h5>
            </Col>
            <Col xs="auto">
              <Dropdown>
                <Dropdown.Toggle variant="success" size="sm">
                  Export
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={exportToExcel}>Excel</Dropdown.Item>
                  <Dropdown.Item onClick={exportToPdf}>PDF</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>

          <Row className="mb-3 g-2 align-items-end">
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label className="fw-bold small">Tax Type</Form.Label>
                <Form.Select
                  value={selectedTax}
                  onChange={(e) => setSelectedTax(e.target.value)}
                >
                  <option value="">All Taxes</option>
                  {taxOptions.map((t, i) => (
                    <option key={i} value={t.tax_name}>
                      {t.tax_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={5}>
              <Form.Label className="fw-bold small">Date Range</Form.Label>
              <DaterangeSelector
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateChange}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="fw-bold small">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by PV code, contractor code or contract no..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
          </Row>
          {/* {JSON.stringify(records)} */}
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <CustomTable fields={fields} data={records} />
          )}
        </Card.Body>
      </Card>
    </ResponsiveContainer>
  );
}

export default TaxReport;
